if (!customElements.get('qr-review-rail')) {
  class QRReviewRail extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.track = this.querySelector('[data-review-track]');
      if (!this.track) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.toggle = this.querySelector('[data-review-toggle]');
      this.previous = this.querySelector('[data-review-previous]');
      this.next = this.querySelector('[data-review-next]');
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.visible = false;
      this.direction = 1;
      this.position = this.track.scrollLeft;
      this.lastTime = 0;
      this.holdUntil = 0;
      this.querySelector('[data-review-controls]').hidden = false;
      this.motion.addEventListener('change', () => this.refresh(), options);
      this.track.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') { this.hovered = true; this.refresh(); } }, options);
      this.track.addEventListener('pointerleave', () => { this.hovered = false; this.refresh(); }, options);
      this.addEventListener('focusin', event => { this.focused = true; this.refresh(); this.revealCard(event.target.closest('.qr-review-card')); }, options);
      this.addEventListener('focusout', () => queueMicrotask(() => { if (this.ready) { this.focused = this.contains(document.activeElement); this.refresh(); } }), options);
      this.track.addEventListener('pointerdown', event => { if (event.pointerType === 'touch' || event.pointerType === 'pen') this.stop(); }, options);
      this.track.addEventListener('wheel', event => { if (Math.abs(event.deltaX) > 0 || event.shiftKey) this.stop(); }, { ...options, passive: true });
      this.track.addEventListener('scroll', () => { if (!this.playing) this.position = this.track.scrollLeft; this.updateArrows(); }, { ...options, passive: true });
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') { this.stop(); this.track.scrollTo({left:event.key === 'Home' ? 0 : this.maximum,behavior:'instant'}); }
        else this.move(event.key === 'ArrowLeft' ? -1 : 1);
      }, options);
      this.toggle.addEventListener('click', () => { this.paused = !this.paused; if (!this.paused) this.focused = false; this.refresh(); }, options);
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      document.addEventListener('visibilitychange', () => this.refresh(), options);
      this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.refresh(); }, {threshold:0.15});
      this.observer.observe(this.track);
      this.resize = new ResizeObserver(() => { this.position = this.track.scrollLeft; this.refresh(); });
      this.resize.observe(this.track);
      this.tick = time => {
        if (!this.playing) return;
        const delta = this.lastTime ? Math.min(time-this.lastTime,50) : 0;
        this.lastTime = time;
        if (time >= this.holdUntil) {
          this.position = Math.max(0,Math.min(this.maximum,this.position+this.direction*20*delta/1000));
          this.track.scrollLeft = this.position;
          if ((this.direction > 0 && this.position >= this.maximum) || (this.direction < 0 && this.position <= 0)) { this.direction *= -1; this.holdUntil = time+2200; }
        }
        this.frame = requestAnimationFrame(this.tick);
      };
      this.refresh();
    }
    get maximum() { return Math.max(0,this.track.scrollWidth-this.track.clientWidth); }
    revealCard(card) {
      if (!card || !this.track.contains(card)) return;
      const viewport = this.track.getBoundingClientRect();
      const bounds = card.getBoundingClientRect();
      const left = viewport.left + this.track.clientLeft + 3;
      const right = viewport.left + this.track.clientLeft + this.track.clientWidth - 3;
      const offset = bounds.left < left ? bounds.left - left : bounds.right > right ? bounds.right - right : 0;
      if (offset) this.track.scrollTo({left:Math.max(0,Math.min(this.maximum,this.track.scrollLeft+offset)),behavior:'instant'});
      this.position = this.track.scrollLeft;
      this.updateArrows();
    }
    stop() { this.paused = true; this.refresh(); }
    move(direction) {
      this.stop();
      const cards = [...this.track.children], current = this.track.scrollLeft;
      const points = cards.map(card => Math.max(0,card.offsetLeft-cards[0].offsetLeft));
      const target = direction > 0 ? points.find(point => point > current+4) ?? this.maximum : points.reverse().find(point => point < current-4) ?? 0;
      this.track.scrollTo({left:Math.min(target,this.maximum),behavior:this.motion.matches ? 'instant' : 'smooth'});
    }
    updateArrows() {
      this.previous.disabled = this.track.scrollLeft <= 1;
      this.next.disabled = this.track.scrollLeft >= this.maximum-1;
    }
    refresh() {
      cancelAnimationFrame(this.frame);
      const shouldPlay = !this.paused && !this.hovered && !this.focused && !this.motion.matches && this.visible && !document.hidden && this.maximum > 1;
      this.playing = shouldPlay;
      this.position = this.track.scrollLeft;
      this.lastTime = 0;
      this.toggle.hidden = this.motion.matches || this.maximum <= 1;
      const label = this.paused ? this.dataset.playLabel : this.dataset.pauseLabel;
      this.toggle.setAttribute('aria-label',label);
      this.querySelector('[data-review-motion-label]').textContent = label;
      this.querySelector('[data-play-icon]').toggleAttribute('hidden',!this.paused);
      this.querySelector('[data-pause-icon]').toggleAttribute('hidden',this.paused);
      this.dataset.playing = String(shouldPlay);
      this.updateArrows();
      if (shouldPlay && this.tick) this.frame = requestAnimationFrame(this.tick);
    }
    disconnectedCallback() {
      this.ready = false;
      this.playing = false;
      cancelAnimationFrame(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
    }
  }
  customElements.define('qr-review-rail',QRReviewRail);
}
if (location.pathname.replace(/\/$/,'').endsWith('/a/review/all') && /^#stamped-review-\d+$/.test(location.hash)) {
  let observer, timeout;
  const reveal = () => {
    const target = document.getElementById(location.hash.slice(1));
    if (!target || !target.offsetHeight) return false;
    observer?.disconnect();
    clearTimeout(timeout);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      target.setAttribute('tabindex','-1');
      target.scrollIntoView({block:'center',behavior:'instant'});
      target.focus({preventScroll:true});
    }));
    return true;
  };
  if (!reveal()) {
    observer = new MutationObserver(reveal);
    observer.observe(document.body,{childList:true,subtree:true});
    timeout = setTimeout(() => observer.disconnect(),20000);
  }
}
